/** @format */
import { ChangeDetectionStrategy, Component, inject, Input, ViewEncapsulation } from '@angular/core';
import { FudisHeadingLevel } from '@funidata/ngx-fudis';
import { SearchResult } from 'common-typescript/types';
import { DEFAULT_SEARCH_LIMIT, MAX_SEARCH_LIMIT } from '../search-constants';
import { SearchService } from '../search.service';

/**
 * Component for showing total count and fetched count of search results. Also supports fetching maximum amount of
 * search results.
 */
@Component({
    selector: 'sis-search-result-counts',
    templateUrl: './search-result-counts.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class SearchResultCountsComponent<T> {
    @Input({ required: true }) searchResult: SearchResult<T>;

    /**
     * Whether the "Show more" buttons is enabled when total results exceed the limit. True by default,
     * can be disabled for example when paging component is used.
     */
    @Input() showMoreEnabled: boolean = true;

    /**
     * When true, each click of "Show more" increases the limit by {@link showMoreStep} (up to {@link MAX_SEARCH_LIMIT})
     * instead of fetching the maximum amount of results at once. Once all results have been fetched, the
     * button changes to "Show less", which resets the limit back to {@link showMoreStep}. False by default.
     */
    @Input() incrementalShowMore: boolean = false;

    /**
     * The amount by which the limit is increased on each "Show more" click when {@link incrementalShowMore} is enabled.
     * Should typically match the initial limit of the search. Defaults to {@link DEFAULT_SEARCH_LIMIT}.
     */
    @Input() showMoreStep: number = DEFAULT_SEARCH_LIMIT;

    /**
     * Optional id for testing purposes and for handling focus when navigating from paginated search results
     */
    @Input() id: string;

    /**
     * Mandatory input for passing the correct heading level used in template.
     */
    @Input({ required: true }) resultHeadingLevel: FudisHeadingLevel;

    /**
     * A view specific instance of {@link SearchService}.
     */
    private searchService = inject(SearchService);

    protected readonly MAX_SEARCH_LIMIT = MAX_SEARCH_LIMIT;

    showMoreOrLessResults(): void {
        if (this.isShowLess()) {
            this.searchService.patchOptions({ limit: this.incrementalShowMore ? this.showMoreStep : DEFAULT_SEARCH_LIMIT });
        } else if (this.incrementalShowMore) {
            this.searchService.patchOptions({
                limit: Math.min((this.searchResult?.limit ?? 0) + this.showMoreStep, MAX_SEARCH_LIMIT),
            });
        } else {
            this.searchService.patchOptions({ limit: MAX_SEARCH_LIMIT });
        }
    }

    /**
     * Whether the toggle should reset the limit instead of increasing it. In incremental mode this is the case once all results
     * have been fetched through "Show more" clicks.
     */
    protected isShowLess(): boolean {
        const limit = this.searchResult?.limit ?? 0;
        if (!this.incrementalShowMore) {
            return limit >= MAX_SEARCH_LIMIT;
        }
        return limit > this.showMoreStep && (limit >= (this.searchResult?.total ?? 0) || limit >= MAX_SEARCH_LIMIT);
    }
}
